import { computeInvoiceDocumentAmounts } from './invoicePdfHtml';

function startOfDay(d) {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
}

/** Amount shown on invoice cards: manual override, else total from invoice PDF data. */
export function getDisplayInvoiceAmount(quote) {
  if (!quote) return null;
  const rawAmt = quote.invoiceAmount ?? quote.invoice_amount;
  if (rawAmt != null && rawAmt !== '') {
    const n = typeof rawAmt === 'number' ? rawAmt : parseFloat(rawAmt);
    if (!Number.isNaN(n)) return n;
  }
  if (quote.invoiceData) {
    try {
      return computeInvoiceDocumentAmounts(quote.invoiceData).total;
    } catch {
      return null;
    }
  }
  return null;
}

/** Legacy: paid vs awaiting (for toggles). */
export function getInvoicePaymentStatus(quote) {
  if (!quote?.invoiceData) return null;
  return quote.invoicePaymentStatus === 'paid' ? 'paid' : 'awaiting';
}

/**
 * Payment pill label + urgency using PDF due date (`invoiceData.dueDate`) and paid flag.
 * - Paid → green
 * - Unpaid + no/invalid due date → amber awaiting
 * - Unpaid + overdue → red
 * - Unpaid + 1–5 calendar days until due (inclusive) or due today → red, days left / overdue text
 * - Unpaid + more than 5 days → amber generic awaiting
 */
export function getInvoicePaymentBadgeInfo(quote) {
  if (!quote?.invoiceData) return null;
  // Only show payment state once the row is “Sent Invoice” (or quote+invoice sent) — hide for Draft / Sent Quote only
  if (quote.status !== 'sent_invoice' && quote.status !== 'sent_both') {
    return null;
  }
  if (quote.invoicePaymentStatus === 'paid') {
    return { variant: 'paid', label: 'Payment complete' };
  }

  const dueRaw = quote.invoiceData.dueDate;
  if (!dueRaw) {
    return { variant: 'awaiting', label: 'Awaiting payment' };
  }

  const due = new Date(dueRaw);
  if (Number.isNaN(due.getTime())) {
    return { variant: 'awaiting', label: 'Awaiting payment' };
  }

  const today = startOfDay(new Date());
  const dueDay = startOfDay(due);
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return {
      variant: 'overdue',
      label: overdue === 1 ? '1 day overdue' : `${overdue} days overdue`,
    };
  }

  if (diffDays <= 5) {
    if (diffDays === 0) {
      return { variant: 'urgent', label: 'Due today — pay now' };
    }
    return {
      variant: 'urgent',
      label: `${diffDays} day${diffDays === 1 ? '' : 's'} left to pay`,
    };
  }

  return { variant: 'awaiting', label: 'Awaiting payment' };
}

const DUE_SOON_DAYS = 7;

/**
 * Due-countdown pill for invoice list rows: shows days until due (from `invoiceData.dueDate`).
 * Orange when due in 0–7 days (inclusive), red when overdue, neutral when farther out.
 * Same visibility as `getInvoicePaymentBadgeInfo` (sent invoice + invoice data).
 * Parent toggles `invoicePaymentStatus` on click — when paid, label is "Paid".
 */
export function getInvoiceDueCountdownBadgeInfo(quote) {
  if (!quote?.invoiceData) return null;
  if (quote.status !== 'sent_invoice' && quote.status !== 'sent_both') {
    return null;
  }
  if (quote.invoicePaymentStatus === 'paid') {
    return { variant: 'paid', label: 'Paid' };
  }

  const dueRaw = quote.invoiceData.dueDate;
  if (!dueRaw) {
    return { variant: 'nodue', label: 'No due date set' };
  }

  const due = new Date(dueRaw);
  if (Number.isNaN(due.getTime())) {
    return { variant: 'nodue', label: 'No due date set' };
  }

  const today = startOfDay(new Date());
  const dueDay = startOfDay(due);
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return {
      variant: 'overdue',
      label: overdue === 1 ? '1 day overdue' : `${overdue} days overdue`,
    };
  }

  if (diffDays <= DUE_SOON_DAYS) {
    if (diffDays === 0) {
      return { variant: 'soon', label: 'Due today' };
    }
    return {
      variant: 'soon',
      label: diffDays === 1 ? 'Due in 1 day' : `Due in ${diffDays} days`,
    };
  }

  return {
    variant: 'upcoming',
    label: `Due in ${diffDays} days`,
  };
}
